import React from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import SEO, { SITE_URL } from '../components/SEO'
import { blogPosts } from '../content/blogposts'
import theme from '../theme'

const BlogIndex: React.FC = () => {
  const history = useHistory()

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "MiAmigo.AI Blog",
    "url": `${SITE_URL}/blog`,
    "blogPost": blogPosts.map((p) => ({
      "@type": "BlogPosting",
      "headline": p.title,
      "url": `${SITE_URL}/blog/${p.slug}`,
      "datePublished": p.date,
    })),
  }

  return (
    <PageContainer>
      <SEO
        title="Blog - AI Chatbots, RAG & Customer Support | MiAmigo.AI"
        description="Practical guides on AI chatbots, retrieval-augmented generation, customer support automation, and getting found by both Google and AI search."
        path="/blog"
        jsonLd={jsonLd}
      />
      <Header>Blog</Header>
      <SubHeader>AI chatbots, customer support, and getting found online.</SubHeader>
      <PostList>
        {blogPosts
          .slice()
          .sort((a, b) => (a.date < b.date ? 1 : -1))
          .map((post) => (
            <PostCard key={post.slug} onClick={() => history.push(`/blog/${post.slug}`)}>
              <PostDate>{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</PostDate>
              <PostTitle>{post.title}</PostTitle>
              <PostDescription>{post.description}</PostDescription>
              <TagRow>
                {post.tags.map((t) => (
                  <Tag key={t}>{t}</Tag>
                ))}
              </TagRow>
            </PostCard>
          ))}
      </PostList>
    </PageContainer>
  )
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 16px 80px;
  min-height: 85vh;
`

const Header = styled.h1`
  font-size: 42px;
  font-weight: 900;
  color: ${theme.colors.text};
  margin: 5px;
`

const SubHeader = styled.div`
  font-size: 16px;
  color: ${theme.colors.textMuted};
  margin-bottom: 30px;
`

const PostList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 720px;
`

const PostCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadow.card};
  padding: 24px;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  &:hover {
    box-shadow: ${theme.shadow.raised};
    transform: translateY(-2px);
  }
`

const PostDate = styled.div`
  font-size: 12px;
  color: ${theme.colors.textMuted};
  margin-bottom: 6px;
`

const PostTitle = styled.h2`
  font-size: 22px;
  font-weight: 800;
  color: ${theme.colors.text};
  margin: 0 0 8px 0;
`

const PostDescription = styled.p`
  font-size: 14px;
  color: ${theme.colors.textMuted};
  line-height: 1.6;
  margin: 0 0 12px 0;
`

const TagRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const Tag = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${theme.colors.primary};
  background: ${theme.colors.primarySoft};
  padding: 3px 8px;
  border-radius: ${theme.radius.sm};
`

export default BlogIndex
